import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { newReportId } from '@/services/reports/reportsApi'

interface CopyReportItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What is being copied, e.g. "report template" — used in the dialog's wording. */
  kind: string
  source: { name: string; description?: string }
  /** Whether the copy has a description to edit. Schedules have none. */
  withDescription?: boolean
  copy: (newId: string, name: string, description: string) => Promise<{ id: string; name: string }>
  /** Query key the list is cached under, refreshed once the copy exists. */
  listKey: string
  /** Where the copy's own page lives, given its id. */
  pathFor: (id: string) => string
}

/** Creates a new template, schedule or translation from an existing one. */
export function CopyReportItemDialog({
  open,
  onOpenChange,
  kind,
  source,
  withDescription = true,
  copy,
  listKey,
  pathFor,
}: CopyReportItemDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState(t('reportsModule.copyName', { name: source.name }))
  const [description, setDescription] = useState(source.description ?? '')
  const [openCopy, setOpenCopy] = useState(true)

  const mutation = useMutation({
    mutationFn: () => copy(newReportId(), name.trim(), description),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [listKey] })
      toast.success(t('reportsModule.copied'), {
        description: t('reportsModule.copiedDetail', { name: created.name }),
        duration: 3000,
      })
      onOpenChange(false)
      if (openCopy) navigate(pathFor(created.id))
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t('reportsModule.copyFailed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const valid = !!name.trim()

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('reportsModule.copyTitle', { kind })}</DialogTitle>
            <DialogDescription>
              {t('reportsModule.copyDescription', { kind, name: source.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-report-item-name">{t('common.name')}</FieldLabel>
            <Input
              id="copy-report-item-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          {withDescription && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="copy-report-item-description">
                {t('common.description')}
              </FieldLabel>
              <Input
                id="copy-report-item-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="copy-report-item-open"
              checked={openCopy}
              onCheckedChange={(v) => setOpenCopy(v === true)}
            />
            <Label htmlFor="copy-report-item-open" className="font-normal">
              {t('reportsModule.openCopy')}
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={!valid}>
              <Copy className="size-4" />
              {t('reportsModule.copy')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
